/**
 * The online withdrawal function (Directive (EU) 2023/2673): for 14 days after
 * a purchase, one clearly labelled button ends the plan and refunds the unused
 * days. Shown only while the backend says the purchase can still be withdrawn
 * from (GET /stripe/withdrawal); the work is done there (withdrawal.service).
 */

import apiClient from "@/services/api/apiClient";
import { useI18nStore } from "@/stores/i18nStore";
import { useLocaleFormat } from "@/hooks/useLocaleFormat";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Undo2 } from "lucide-react";
import React, { useState } from "react";

interface WithdrawalStatus {
  eligible: boolean;
  deadline: string | null;
  trialing: boolean;
  refund: { amount: number; currency: string } | null;
}

interface WithdrawalResult {
  refund: { amount: number; currency: string } | null;
  refundState: "refunded" | "pending" | "none";
}

const QUERY = ["withdrawal-status"] as const;

export const WithdrawalCard: React.FC<{ onWithdrawn?: () => void }> = ({
  onWithdrawn,
}) => {
  const { t } = useI18nStore();
  const fmt = useLocaleFormat();
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const { data } = useQuery({
    queryKey: QUERY,
    queryFn: async () =>
      (await apiClient.get("/stripe/withdrawal")).data.data as WithdrawalStatus,
    retry: false,
  });
  const withdraw = useMutation({
    mutationFn: async () =>
      (await apiClient.post("/stripe/withdrawal")).data
        .data as WithdrawalResult,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY });
      onWithdrawn?.();
    },
  });

  const money = (r: { amount: number; currency: string }) =>
    new Intl.NumberFormat(fmt.locale, {
      style: "currency",
      currency: r.currency.toUpperCase(),
    }).format(r.amount / 100);

  if (withdraw.data) {
    const { refund, refundState } = withdraw.data;
    const refundLine =
      refund && refund.amount > 0
        ? (refundState === "refunded"
            ? t.withdrawRefundIssued
            : t.withdrawRefundPending
          ).replace("{amount}", money(refund))
        : "";
    return (
      <div
        role="status"
        className="rounded-xl border border-gray-200 bg-white p-6 text-sm text-gray-700"
      >
        {t.withdrawDone.replace("{refund}", refundLine)}
      </div>
    );
  }

  if (!data?.eligible || !data.deadline) return null;

  const deadline = fmt.date(data.deadline, "long");

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-6">
      <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
        <Undo2 className="h-5 w-5 text-gray-500" aria-hidden="true" />
        {t.withdrawTitle}
      </h2>
      <p className="mt-1 text-sm text-gray-600">
        {t.withdrawLead.replace("{deadline}", deadline)}{" "}
        {data.trialing
          ? t.withdrawTrial
          : data.refund && data.refund.amount > 0
            ? t.withdrawRefund.replace("{amount}", money(data.refund))
            : ""}
      </p>

      {confirming ? (
        <div className="mt-4 space-y-3">
          <p className="text-sm font-medium text-gray-900">
            {t.withdrawConfirm}
          </p>
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              disabled={withdraw.isPending}
              onClick={() => withdraw.mutate()}
              className="rounded-lg bg-ink px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
            >
              {t.withdrawConfirmYes}
            </button>
            <button
              type="button"
              onClick={() => setConfirming(false)}
              className="rounded-lg px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100"
            >
              {t.immediateStartBack}
            </button>
          </div>
          {withdraw.isError && (
            <p role="alert" className="text-sm font-medium text-red-700">
              {t.withdrawFailed}
            </p>
          )}
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setConfirming(true)}
          className="mt-4 rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-900 hover:bg-gray-50"
        >
          {t.withdrawButton}
        </button>
      )}
    </div>
  );
};

export default WithdrawalCard;
